import { message } from 'antd';
import { useState } from 'react';

import useAPIClient from '../../api/useAPIClient';
import { AsyncSinapseResponseStatus, SinapseResult } from '../api';
import {
  useAsyncSendText,
  useCancelPollingSynapseResponse,
  useCancelPollingTextOptimizingStatus,
  useGetSinapseResponseStatus,
} from '../asyncTextOptimize';
import { SinapseAction } from '../constants/actions';

type ProcessResultArgs = { overwriteOnSuccess?: boolean; queryKeyPath?: (number | string)[] };

export interface AsyncSinapseResponseHookProps<R = SinapseResult> {
  setIsDisabledInput?: React.Dispatch<React.SetStateAction<boolean>>;
  setResult?: (result: R) => void;
  sourceId?: number;
  textValue?: string;
  action?: SinapseAction;
  enabled?: boolean;
  input?: string;
  configs?: ProcessResultArgs;
}

interface AsyncSinapseResponseHookReturn<R = SinapseResult> {
  overwriteCurrentWithResponseResult?: () => void;
  discartResponseResult?: () => void;
  processResult?: () => void;
  responseStatus: AsyncSinapseResponseStatus['status'] | '';
  generatedResult?: AsyncSinapseResponseStatus<R>['result'];
  enabled?: boolean;
}

const useAsyncSinapseResponse = <R = SinapseResult>({
  setIsDisabledInput,
  setResult,
  sourceId,
  action = 'text',
  enabled = false,
  input = '',
  configs = { overwriteOnSuccess: false, queryKeyPath: undefined },
}: AsyncSinapseResponseHookProps<R>): AsyncSinapseResponseHookReturn<R> => {
  const [taskId, setTaskId] = useState<string>('');
  const client = useAPIClient();
  const { cancel: cancelPolling } = useCancelPollingSynapseResponse({
    queryKey: configs.queryKeyPath,
  });

  const { mutateAsync, isError } = useAsyncSendText(client, {
    onSuccess: (data) => {
      setTaskId(data.task_id);
    },
    onError: (error: unknown) => {
      setIsDisabledInput?.(false);
      if (error && typeof error === 'object' && 'response' in error) {
        const responseError = error as { response?: { data?: string[] } };
        responseError.response?.data?.map((msg: string) => {
          message.error(msg);
        });
      }
    },
  });

  const processText = () => {
    setIsDisabledInput?.(true);
    mutateAsync({ input, source: sourceId, action });
  };

  const { data: { status: responseStatus = '', result: generatedResult } = {} } =
    useGetSinapseResponseStatus<R>(client, taskId, {
      onError: () => {
        setIsDisabledInput?.(false);
      },
      onSuccess: (data) => {
        if (data.status === 'completed') {
          setIsDisabledInput?.(false);
          if (configs.overwriteOnSuccess) {
            setResult?.(data?.result);
          }
          setTaskId('');
          cancelPolling();
        }
        if (data.status === 'failed') {
          setIsDisabledInput?.(false);
          message.error('Erro ao processar texto com as Synapses!');
        }
      },
      enabled: !!taskId && enabled,
      queryKey: configs.queryKeyPath || undefined,
    });

  const { cancel: cancelOptimizeTextStatus } = useCancelPollingTextOptimizingStatus({
    task_id: taskId,
  });

  const overwriteCurrentWithResponseResult = () => {
    if (generatedResult) {
      setResult?.(generatedResult);
    }
    setIsDisabledInput?.(false);
  };

  const discartResponseResult = () => {
    cancelOptimizeTextStatus();
    setIsDisabledInput?.(false);
    setTaskId('');
  };

  if (!enabled) {
    return {
      overwriteCurrentWithResponseResult: undefined,
      discartResponseResult: undefined,
      processResult: undefined,
      responseStatus: '',
      generatedResult: undefined,
      enabled: false,
    };
  }

  return {
    overwriteCurrentWithResponseResult,
    discartResponseResult,
    processResult: processText,
    responseStatus: !isError ? responseStatus : 'failed',
    generatedResult,
    enabled,
  };
};

export type AsyncSinapseResponseHook = typeof useAsyncSinapseResponse;

export default useAsyncSinapseResponse;
